
import React from "react";
import { Link } from "wouter";

import {
  UsersIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

import EmployeesMultiSelectSearchbar from "./employee_multiselect_searchbar";
import V2ListBoxPageSize from "./v2_listbox_page_size";


const EMPLOYEE_FILTERS = [
  { value: "all", label: "Tutti" },
  { value: "active", label: "Contratti attivi" },
  { value: "inactive", label: "Contratti non attivi" },
  { value: "app_activate", label: "Accesso app" },
  { value: "hr_active", label: "App e contratto attivo" },
];

const COLUMN_OPTIONS = [
  { name: "name", label: "Cognome e nome" },
  { name: "email_auth", label: "Email" },
  { name: "start_date", label: "Data inizio" },
  { name: "end_date", label: "Data fine" },
];

const FILTER_LABELS = {
  all: "Totale dipendenti",
  active: "Contratti attivi",
  inactive: "Contratti non attivi",
  app_activate: "Accesso app abilitato",
  hr_active: "App e contratto attivo",
};


export default function EmployeesListControls({
  totalResult = 0,

  filter = "all",
  onFilterChange,

  token,
  selectedEmployee = null,
  getEmployees,
  onSelectEmployee,

  pageSize = 25,
  onPageSizeChange,

  showFilters = false,
  onToggleFilters,

  visibleColumns = {},
  onColumnChange,

  contractStartDate = "",
  contractEndDate = "",
  onContractStartDateChange,
  onContractEndDateChange,
  onSearchDates,

  onExport,
  className = "",
}) {

  const activeFilterCount =
    Number(filter !== "all") +
    Number(Boolean(contractStartDate)) +
    Number(Boolean(contractEndDate));

  // La modalità compatta restituisce comunque un array
  // per mantenere compatibile l'API del componente.
  const handleEmployeeChange = (employees) => {
    const employee = employees?.[0] || null;
    onSelectEmployee?.(employee);
  };


  return (
    <section
      className={`relative mb-5 rounded-xl border border-gray-300 shadow-sm ${className}`}
    >

      {/* INTESTAZIONE */}
      <div className="flex flex-col rounded-t-xl gap-4 border-b border-gray-100 bg-indigo-100 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">

        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50/90 border border-indigo-300 text-indigo-700">
            <UsersIcon className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <h2 className="text-base font-semibold uppercase text-indigo-800">
              Gestione dipendenti
            </h2>

            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">
                {FILTER_LABELS[filter] || "Dipendenti"}
              </span>

              <span className="inline-flex rounded-full bg-indigo-600 border border-indigo-300 px-2 py-0.5 text-xs font-bold text-white tabular-nums">
                {totalResult}
              </span>
            </div>
          </div>
        </div>


        {/* AZIONI */}
        <div className="flex flex-wrap items-center gap-2">

          <Link
            to="/"
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 sm:text-sm"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Indietro
          </Link>

          <Link
            to="/nuovo_dipendente"
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white transition hover:bg-indigo-500 sm:text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            Aggiungi
          </Link>

          <button
            type="button"
            onClick={onExport}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 sm:text-sm"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Esporta
          </button>

        </div>
      </div>


      {/* BARRA OPERATIVA */}
      <div className="
        grid grid-cols-1
        gap-x-4 gap-y-4
        px-4 py-4 sm:px-5
        sm:grid-cols-2
        lg:grid-cols-12
        lg:items-end
      ">

        {/* RICERCA DIPENDENTE */}
        <div className="
          relative z-20 min-w-0 w-full
          sm:col-span-1
          lg:col-span-6
        ">

          <div className="mb-1.5 flex items-center justify-between gap-2">

            <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <MagnifyingGlassIcon className="h-4 w-4 text-indigo-600" />
              Ricerca dipendente
            </label>

            {selectedEmployee && (
              <button
                type="button"
                onClick={() => onSelectEmployee?.(null)}
                className="shrink-0 text-xs font-semibold text-indigo-600 transition-colors hover:text-indigo-800 hover:underline focus:outline-none"
              >
                Reset
              </button>
            )}

          </div>

          <EmployeesMultiSelectSearchbar
            token={token}
            selectedEmployees={
              selectedEmployee ? [selectedEmployee] : []
            }
            onChange={handleEmployeeChange}
            getEmployees={getEmployees}
            filterBy="all"
            pageSize={10}
            placeholder="Digita per cercare il dipendente..."
            positionOptions="absolute"
            compactSelection={true}
            className="w-full"
          />

        </div>


        {/* APERTURA FILTRI */}
        <div className="
          flex min-w-0 flex-col
          sm:col-span-1
          lg:col-span-4
        ">

          {/* Spazio per allineamento con le altre etichette */}
          <div
            className="mb-1.5 hidden h-4 sm:block"
            aria-hidden="true"
          />

          <button
            type="button"
            onClick={onToggleFilters}
            aria-expanded={showFilters}
            aria-controls="employees-list-filters"
            className={`
              flex h-10 w-full shrink-0
              items-center justify-center gap-2
              rounded-lg border px-4
              text-sm font-semibold transition
              ${
                showFilters
                  ? "border-indigo-300 bg-indigo-100 text-indigo-800"
                  : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
              }
            `}
          >

            <AdjustmentsHorizontalIcon className="h-5 w-5 shrink-0" />

            Filtri

            {activeFilterCount > 0 && (
              <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}

            <ChevronDownIcon
              className={`h-4 w-4 shrink-0 transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />

          </button>
        </div>


        {/* PAGINAZIONE */}
        <div className="
          relative z-10 min-w-0 
          sm:col-span-2
          lg:col-span-2
        ">
          <div className="flex h-10 items-center">
            <V2ListBoxPageSize
              value={pageSize}
              onChange={onPageSizeChange}
              compactHeight={true}
            />
          </div>

        </div>

      </div>


      {/* PANNELLO FILTRI */}
      {showFilters && (
        <div
          id="employees-list-filters"
          className="border-t border-gray-200 bg-gray-50/40 px-4 py-5 sm:px-5"
        >

          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase text-indigo-800">
              Filtri elenco
            </h3>

            <p className="mt-0.5 text-xs text-gray-500">
              Seleziona i dipendenti, i dettagli e il periodo da visualizzare
            </p>
          </div>


          <div className="grid grid-cols-1 gap-2">
            <div className="border-b border-gray-200 pb-4">
              {/* FILTRO DIPENDENTI */}
              <fieldset className="min-w-0">
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Visualizza dipendenti
                </legend>

                <div className="flex flex-wrap gap-2">

                  {EMPLOYEE_FILTERS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => onFilterChange?.(value)}
                      aria-pressed={filter === value}
                      className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                        filter === value
                          ? "border-indigo-300 bg-indigo-100 text-indigo-800"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {label}
                    </button>
                  ))}

                </div>
              </fieldset>
            </div>

            {/* COLONNE VISIBILI */}
            <fieldset className="min-w-0">

              <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                Dettagli da visualizzare
              </legend>

              <div className="flex flex-wrap gap-x-5 gap-y-3">

                {COLUMN_OPTIONS.map(({ name, label }) => (
                  <label
                    key={name}
                    className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                  >

                    <input
                      type="checkbox"
                      name={name}
                      checked={Boolean(visibleColumns[name])}
                      onChange={onColumnChange}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />

                    {label}

                  </label>
                ))}

              </div>
            </fieldset>

          </div>


          {/* PERIODO CONTRATTO */}
          <div className="mt-5 border-t border-gray-200 pt-4">

            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Periodo contratto
            </h4>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">

              <div className="w-full sm:w-44">

                <label
                  htmlFor="employees-contract-start"
                  className="mb-1.5 block text-xs font-medium text-gray-600"
                >
                  Data inizio
                </label>

                <input
                  id="employees-contract-start"
                  type="date"
                  value={contractStartDate}
                  onChange={(event) =>
                    onContractStartDateChange?.(
                      event.target.value
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />

              </div>


              <div className="w-full sm:w-44">

                <label
                  htmlFor="employees-contract-end"
                  className="mb-1.5 block text-xs font-medium text-gray-600"
                >
                  Data fine
                </label>

                <input
                  id="employees-contract-end"
                  type="date"
                  value={contractEndDate}
                  onChange={(event) =>
                    onContractEndDateChange?.(
                      event.target.value
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />

              </div>


              <button
                type="button"
                onClick={onSearchDates}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-500"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                Cerca
              </button>

            </div>
          </div>

        </div>
      )}

    </section>
  );
}
